import classNames from 'classnames';
import { Field, Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import getValidator from '../../../utils/getValidator';
import { pushErrorTrackingEvent } from '../../../utils/tracking';
import Button from '../../atoms/Button';
import Price from '../../atoms/Price';
import InputWithIconAndButtonField from '../../formik/InputWithIconAndButtonField';
import { showDefaultError, showNotification } from '../Notifications';
import './style.scss';

const VoucherList = () => {
  const { checkoutState, addVoucher, removeVoucher } = useCheckoutState();
  const { formatMessage } = useIntl();
  const [isLoading, setLoading] = useState({ add: false, remove: false, id: '' });
  const { vouchers = [] } = checkoutState.orderState;

  const isGiftcardsEnabled = config.features.giftCards.enabled;
  const { maxLength } = config.features.vouchers;

  const initialValues = {
    voucherCode: '',
  };

  const statusCodeI18nKeys = {
    201: 'payment.voucher.notifications.success',
    404: 'payment.voucher.notifications.notFound',
    417: 'payment.voucher.notifications.orderUpdateError',
  };

  const statusCodeTrackingEventAction = {
    404: 'general form',
    417: 'general form',
  };

  const addHandler = async ({ voucherCode }: typeof initialValues) => {
    if (voucherCode === '') {
      return;
    }
    setLoading({ add: true, remove: false, id: '' });
    try {
      const vouchersResponse = await addVoucher({ code: voucherCode });
      if (vouchersResponse?.length) {
        showNotification({
          level: 'success',
          text: formatMessage({ id: statusCodeI18nKeys[201] }, { name: vouchersResponse[0].name }),
          autoClose: true,
          singleInstance: true,
          reference: 'voucher',
        });
      }
    } catch (e: any) {
      const error = statusCodeI18nKeys[e.statusCode];
      if (error) {
        const message = formatMessage({ id: error });
        pushErrorTrackingEvent(statusCodeTrackingEventAction[e.statusCode], 'error', message);

        showNotification({
          level: 'error',
          text: message,
          autoClose: true,
          closable: true,
          singleInstance: true,
          reference: 'voucher',
        });
      } else {
        console.error(e);
        showDefaultError();
      }
    }
    setLoading({ add: false, remove: false, id: '' });
  };

  const removeHandler = async (id: string) => {
    setLoading({ add: false, remove: true, id });
    try {
      await removeVoucher({ id });
    } catch (e) {
      console.error(e);
      showDefaultError();
    }
    setLoading({ add: false, remove: false, id: '' });
  };

  return (
    <>
      {!!vouchers.length && (
        <div className={classNames('voucher__list', { 'voucher__list--with-giftcards': isGiftcardsEnabled })}>
          {vouchers.map(({ id, name, discount }) => (
            // TODO: Abstract this part into a reusable "ActiveBenefit" component or something like that
            <div className="voucher__list-voucher" key={id}>
              {/* <img className="voucher__list-voucher--icon" src={getAssetUrl('inputs/voucher.svg')} alt={'voucher'} /> */}
              <div className="content">
                <span className="voucher__list-voucher--name">{name}</span>
                <span className="voucher__list-voucher--discount">
                  <Price price={discount * -1} />
                </span>
                <Button
                  type="button"
                  className="voucher__list-voucher--btn"
                  onClick={() => removeHandler(id)}
                  isLoading={isLoading.remove && id === isLoading.id}
                >
                  <FormattedMessage id="generic.remove" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!vouchers.length && (
        <Formik onSubmit={addHandler} initialValues={initialValues}>
          {({ submitForm }) => (
            <Field
              component={InputWithIconAndButtonField}
              button={{
                text: 'generic.add',
                onClick: submitForm,
              }}
              placeholder={formatMessage({ id: 'payment.voucher.label' })}
              className={classNames('voucher__input', {
                'voucher__input--with-vouchers': !!vouchers.length,
                'voucher__input--with-giftcard': isGiftcardsEnabled,
              })}
              name="voucherCode"
              isLoading={isLoading.add}
              maxLength={maxLength}
              validate={getValidator(config.features.vouchers)}
              isInputStatusIconDisabled
            />
          )}
        </Formik>
      )}
    </>
  );
};

export default VoucherList;
